'use client';

import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { primeUserPreviewCacheFromPreview, resolveUserPreviewCacheKey } from '@/lib/cache/user-preview-cache';
import { useLogger } from '@/lib/client-logger';
import { useFollowActions } from '@/lib/hooks/api/social-actions';
import { isMeaningfulLocation } from '@/lib/location/normalize';
import { isVitest } from '@/lib/test-env';
import { cleanUsername } from '@/lib/utils/username.ts';
import type { UserPreviewVM } from '@/lib/view-models/user-preview';
import { userPreviewStore } from '@/store/user-preview/user-preview-store';
import { HOVER_INTENT_DELAY_MS, PLACEHOLDER_USERNAME } from './constants';
import { useActiveHoverId, useHoverCardCoordinator } from './hover-card-coordinator';
import type { FollowState, HoverPhase, PreviewMetrics } from './types';
import { useBannerReady } from './useBannerReady';
import { type UserData, usePrefetchUser } from './usePrefetchUser';
import { type PreviewHoverTimersConfig, usePreviewHoverTimers } from './usePreviewHoverTimers';

export interface UseUserPreviewStateOptions {
  prefetched?: Partial<UserPreviewVM>;
  profileId?: string | null;
  username: string;
}

export type { FollowState, PreviewMetrics } from './types';

export interface UseUserPreviewStateResult {
  bannerReady: boolean;
  error: boolean;
  followState: FollowState;
  formatJoinedAt: (date: Date) => string;
  handleHoverCardOpenChange: (next: boolean) => void;
  handlePointerEnter: () => void;
  handlePointerLeave: () => void;
  handleUnmount: () => void;
  hoverCardOpen: boolean;
  loading: boolean;
  locationIsLinkable: boolean;
  lookupAvailable: boolean;
  metrics: PreviewMetrics;
  openDelay: number;
  shouldRenderCard: boolean;
  showFallbackLoading: boolean;
  twitterProfileUrl: string | null;
  user: UserData | null;
}

// Hover flow mirrors the user journey: pointer enters (priming), content ready (open),
// linger padding after exit (closing), then hard reset back to idle. Keeping phases here
// lets prefetch, render, and coordinator logic stay in sync without scattered booleans.

function hasSufficientPrefetched(data: Partial<UserData> | undefined): data is UserData {
  if (!data) return false;
  return (
    typeof data.id === 'string' &&
    typeof data.username === 'string' &&
    typeof data.name === 'string' &&
    typeof data.verifiedType === 'string'
  );
}

const isUserStateUpdater = (
  action: SetStateAction<UserData | null>
): action is (previous: UserData | null) => UserData | null => typeof action === 'function';

export function useUserPreviewState({
  username,
  profileId: profileIdProperty,
  prefetched,
}: UseUserPreviewStateOptions): UseUserPreviewStateResult {
  const previewId = useId();
  const coordinator = useHoverCardCoordinator();
  const prefetchedHasData = hasSufficientPrefetched(prefetched);
  // Seed the local state synchronously; cache priming now happens in the layout effect below so the
  // render pass doesn't perform store writes.
  const [user, setUserState] = useState<UserData | null>(() => {
    if (prefetchedHasData && prefetched) {
      return prefetched;
    }
    return null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [phase, setPhase] = useState<HoverPhase>('idle');
  const [pointerInside, setPointerInside] = useState(false);
  const [fastGatePassed, setFastGatePassed] = useState(() => prefetchedHasData);
  const [showFallbackLoading, setShowFallbackLoading] = useState(false);

  const fastGateTimerReference = useRef<number | null>(null);
  const slowFallbackTimerReference = useRef<number | null>(null);
  const closeTimerReference = useRef<number | null>(null);
  // Radix may deliver `onOpenChange(true)` even if the pointer has already bailed (delay expired).
  // Use an imperative ref so we can ignore those stale callbacks without tearing down React state.
  const pointerInsideReference = useRef(false);
  const hoverLogger = useLogger('hover');
  const hasLoggedHoverReference = useRef(false);

  const normalizedPropertyUsername = cleanUsername(username);
  const normalizedPrefetchedUsername =
    prefetched && typeof prefetched.username === 'string' ? cleanUsername(prefetched.username) : '';
  const lookupProfileId = profileIdProperty ?? prefetched?.id ?? null;
  const lookupUsername = normalizedPropertyUsername || normalizedPrefetchedUsername || null;
  const cacheKey = resolveUserPreviewCacheKey({ profileId: lookupProfileId, username: lookupUsername }) ?? null;

  const { user: currentUser } = useAuth();
  const currentUserHandle = cleanUsername(
    typeof currentUser?.twitterUsername === 'string' ? currentUser.twitterUsername : null
  );

  const profileId = user?.id ?? lookupProfileId ?? null;
  const canonicalUsername =
    user?.username ??
    (typeof prefetched?.username === 'string' ? prefetched.username : null) ??
    lookupUsername ??
    normalizedPropertyUsername;
  const isSelf =
    currentUserHandle && canonicalUsername ? cleanUsername(canonicalUsername) === currentUserHandle : false;

  const followsYou =
    (typeof user?.followsYou === 'boolean' ? user.followsYou : undefined) ??
    (typeof prefetched?.followsYou === 'boolean' ? prefetched.followsYou : undefined) ??
    false;
  const initialFollowState = (() => {
    if (typeof prefetched?.isFollowing === 'boolean') {
      return prefetched.isFollowing;
    }
    if (typeof user?.isFollowing === 'boolean') {
      return user.isFollowing;
    }
    return null;
  })();
  const followUsername = cleanUsername(canonicalUsername ?? null) || PLACEHOLDER_USERNAME;
  const lookupAvailable = Boolean(lookupProfileId || lookupUsername);

  const externalUsername = cleanUsername(canonicalUsername ?? null);
  const twitterProfileUrl = externalUsername ? `https://twitter.com/${externalUsername}` : null;

  const locationValue = user?.location ?? null;
  const locationIsLinkable = typeof locationValue === 'string' && isMeaningfulLocation(locationValue);

  const {
    isFollowing,
    isLoading: isFollowLoading,
    toggleFollow,
  } = useFollowActions(profileId ?? null, followUsername, initialFollowState);

  let relationshipType: 'mutual' | 'following' | 'followed_by' | 'none' = 'none';
  if (isFollowing && followsYou) {
    relationshipType = 'mutual';
  } else if (isFollowing) {
    relationshipType = 'following';
  } else if (followsYou) {
    relationshipType = 'followed_by';
  }

  let followButtonText = 'Follow';
  if (isFollowing) {
    followButtonText = 'Following';
  } else if (relationshipType === 'followed_by') {
    followButtonText = 'Follow back';
  }

  const setUser = useCallback<Dispatch<SetStateAction<UserData | null>>>((next) => {
    setUserState((current) => {
      if (isUserStateUpdater(next)) {
        const resolved = next(current);
        return resolved ?? null;
      }
      return next ?? null;
    });
  }, []);

  const useClientLayoutEffect = globalThis.window === undefined ? useEffect : useLayoutEffect;

  const primablePreview = useMemo<UserData | null>(() => {
    if (user) {
      return user;
    }
    if (prefetchedHasData && prefetched) {
      return prefetched;
    }
    return null;
  }, [user, prefetched, prefetchedHasData]);

  const lastPrimedPreviewIdReference = useRef<string | null>(null);

  // Prime the persistence layer after commit; attempting this mirror during render triggered
  // React's "update while rendering" warning because the Zustand store notifies `UserPreview`
  // subscribers immediately.
  useClientLayoutEffect(() => {
    if (!primablePreview) {
      lastPrimedPreviewIdReference.current = null;
      return;
    }

    const cacheLookupKey = cacheKey ?? primablePreview.id;
    if (!cacheLookupKey) {
      return;
    }

    const primedFingerprint = `${cacheLookupKey}:${primablePreview.id}`;
    if (lastPrimedPreviewIdReference.current === primedFingerprint) {
      return;
    }

    const existing = userPreviewStore.getState().getEntry(cacheLookupKey);
    if (existing?.preview && existing.preview.id === primablePreview.id && !existing.error) {
      lastPrimedPreviewIdReference.current = primedFingerprint;
      return;
    }

    primeUserPreviewCacheFromPreview(primablePreview);
    lastPrimedPreviewIdReference.current = primedFingerprint;
    // Previously we re-primed on every render, which rebuilt the cache entry (with a fresh fetchedAt) and triggered an infinite setState loop.
  }, [cacheKey, primablePreview]);

  const prefetchActive = isVitest() ? lookupAvailable : phase === 'priming' || phase === 'open' || phase === 'closing';

  const prefetchOptions: Parameters<typeof usePrefetchUser>[0] = prefetched
    ? {
        primed: prefetchActive,
        lookupProfileId,
        lookupUsername,
        prefetched,
        prefetchedHasData,
        cacheKey,
        profileIdProp: profileIdProperty,
        setUser,
        setError,
        setLoading,
      }
    : {
        primed: prefetchActive,
        lookupProfileId,
        lookupUsername,
        prefetchedHasData,
        cacheKey,
        profileIdProp: profileIdProperty,
        setUser,
        setError,
        setLoading,
      };

  usePrefetchUser(prefetchOptions);

  const fallbackBanner = prefetched?.profileBannerUrl ?? null;
  const bannerCandidate = user?.profileBannerUrl ?? fallbackBanner;
  const isBannerReady = useBannerReady(bannerCandidate);

  const isDataReady = !loading && (Boolean(user) || error);

  const activeHoverId = useActiveHoverId(coordinator.getCurrent());
  const isCurrentHover = activeHoverId === previewId;

  const clearPrimingTimers = useCallback(() => {
    if (fastGateTimerReference.current) {
      globalThis.clearTimeout(fastGateTimerReference.current);
      fastGateTimerReference.current = null;
    }
    if (slowFallbackTimerReference.current) {
      globalThis.clearTimeout(slowFallbackTimerReference.current);
      slowFallbackTimerReference.current = null;
    }
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerReference.current) {
      globalThis.clearTimeout(closeTimerReference.current);
      closeTimerReference.current = null;
    }
  }, []);

  const resetHoverState = useCallback(() => {
    clearPrimingTimers();
    clearCloseTimer();
    setPhase('idle');
    setPointerInside(false);
    pointerInsideReference.current = false;
    setFastGatePassed(prefetchedHasData);
    setShowFallbackLoading(false);
  }, [clearPrimingTimers, clearCloseTimer, prefetchedHasData]);

  usePreviewHoverTimers({
    phase,
    pointerInside,
    prefetchedHasData,
    isDataReady,
    fastGatePassed,
    activeHoverId,
    isCurrentHover,
    previewId,
    coordinator,
    clearPrimingTimers,
    clearCloseTimer,
    resetHoverState,
    setFastGatePassed,
    setShowFallbackLoading,
    setPhase,
    fastGateTimerRef: fastGateTimerReference,
    slowFallbackTimerRef: slowFallbackTimerReference,
    closeTimerRef: closeTimerReference,
    hasLoggedHoverRef: hasLoggedHoverReference,
  } satisfies PreviewHoverTimersConfig);

  const hoverCardOpen = phase === 'open' || phase === 'closing';
  const shouldRenderCard = hoverCardOpen;
  const openDelay = isVitest() ? 0 : HOVER_INTENT_DELAY_MS;

  const metrics: PreviewMetrics = useMemo(() => {
    const selectNumber = (candidate?: number, fallback?: number) => {
      if (typeof candidate === 'number') return candidate;
      return typeof fallback === 'number' ? fallback : undefined;
    };

    const resolved: PreviewMetrics = {};
    const resolvedFollowing = selectNumber(user?.followingCount, prefetched?.followingCount);
    if (typeof resolvedFollowing === 'number') {
      resolved.followingCount = resolvedFollowing;
    }
    const resolvedFollowers = selectNumber(user?.followersCount, prefetched?.followersCount);
    if (typeof resolvedFollowers === 'number') {
      resolved.followersCount = resolvedFollowers;
    }
    const resolvedTweetCount = selectNumber(user?.tweetCount, prefetched?.tweetCount);
    if (typeof resolvedTweetCount === 'number') {
      resolved.tweetCount = resolvedTweetCount;
    }
    const resolvedInteractions = selectNumber(user?.totalInteractions, prefetched?.totalInteractions);
    if (typeof resolvedInteractions === 'number') {
      resolved.interactionsCount = resolvedInteractions;
    }
    return resolved;
  }, [user, prefetched]);

  const handlePointerEnter = useCallback(() => {
    clearCloseTimer();
    setPointerInside(true);
    pointerInsideReference.current = true;
    setPhase((current) => {
      if (current === 'closing') {
        return 'open';
      }
      if (current === 'idle') {
        return 'priming';
      }
      return current;
    });
  }, [clearCloseTimer]);

  const handlePointerLeave = useCallback(() => {
    setPointerInside(false);
    pointerInsideReference.current = false;
    setPhase((current) => {
      if (current === 'open') {
        return 'closing';
      }
      return 'idle';
    });
  }, []);

  const handleHoverCardOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        if (!pointerInsideReference.current) {
          // Stale Radix callback – pointer already left. Abort without opening so timers stay cancelled.
          coordinator.close(previewId);
          hasLoggedHoverReference.current = false;
          resetHoverState();
          return;
        }
        coordinator.open(previewId);
        if (!hasLoggedHoverReference.current) {
          hoverLogger.info('Profile hover opened', {
            username: canonicalUsername ?? lookupUsername ?? null,
            profileId,
          });
          hasLoggedHoverReference.current = true;
        }
      } else {
        if (pointerInsideReference.current) {
          // Ignore Radix close events that race ahead of our hover state; the pointer is still inside.
          return;
        }
        coordinator.close(previewId);
        hasLoggedHoverReference.current = false;
      }
    },
    [coordinator, previewId, hoverLogger, canonicalUsername, lookupUsername, profileId, resetHoverState]
  );

  const handleUnmount = useCallback(() => {
    coordinator.close(previewId);
    hasLoggedHoverReference.current = false;
    clearPrimingTimers();
    clearCloseTimer();
  }, [coordinator, previewId, clearPrimingTimers, clearCloseTimer]);

  const formatJoinedAt = useCallback(
    (date: Date) =>
      new Intl.DateTimeFormat('en-US', {
        month: 'long',
        year: 'numeric',
      }).format(date),
    []
  );

  return {
    lookupAvailable,
    hoverCardOpen,
    openDelay,
    shouldRenderCard,
    showFallbackLoading,
    loading,
    error,
    user,
    twitterProfileUrl,
    followState: {
      isSelf,
      isFollowing,
      isFollowLoading,
      followButtonText,
      toggleFollow,
    },
    metrics,
    locationIsLinkable,
    bannerReady: isBannerReady,
    handleHoverCardOpenChange,
    handlePointerEnter,
    handlePointerLeave,
    handleUnmount,
    formatJoinedAt,
  };
}
